import React, { useState } from "react";
import PropTypes from "prop-types";
import { Card, CardBody, CardHeader } from "reactstrap";

const PlanDistributionChart = ({ data = [] }) => {
    const [hoveredIdx, setHoveredIdx] = useState(null);

    if (!data || data.length === 0) {
        return (
            <Card 
                className="neo-stat-card h-100 animate__animated animate__fadeIn" 
                style={{ 
                    borderRadius: "16px", 
                    border: "2px solid #482712", 
                    boxShadow: "0 15px 40px rgba(72, 39, 18, 0.08)",
                    background: "#fff",
                    fontFamily: "'Outfit', 'Inter', sans-serif"
                }}
            >
                <CardHeader className="p-4 bg-transparent border-bottom-0 pb-1">
                    <div className="d-flex align-items-center gap-2">
                        <div
                            style={{ 
                                width: "38px", 
                                height: "38px", 
                                minWidth: "38px",
                                borderRadius: "10px",
                                background: "rgba(226, 102, 28, 0.08)",
                                border: "1px solid rgba(226, 102, 28, 0.20)",
                                color: "#e2661c",
                                display: "flex",
                                alignItems: "center",
                                justifyToContent: "center"
                            }}
                            className="d-flex align-items-center justify-content-center"
                        >
                            <i className="mdi mdi-chart-donut font-size-18"></i>
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold font-size-14 text-uppercase" style={{ color: "#482712", letterSpacing: "0.5px" }}>
                                Distribuição de Planos
                            </h5>
                            <small className="text-muted font-size-11 fw-semibold d-block">
                                Breakdown de contas ativas do SaaS
                            </small>
                        </div>
                    </div>
                </CardHeader>
                <CardBody className="p-4 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "220px" }}>
                    <div className="text-center text-muted">
                        <i className="mdi mdi-chart-arc font-size-36 mb-2 d-block" style={{ color: "rgba(72, 39, 18, 0.2)" }}></i>
                        <span className="font-size-12 fw-semibold">Sem dados de distribuição de planos cadastrados</span>
                    </div>
                </CardBody>
            </Card>
        );
    }

    const chartData = data;
    const totalClients = chartData.reduce((acc, item) => acc + item.value, 0);

    // Configuração do Donut SVG
    const radius = 35;
    const circumference = 2 * Math.PI * radius; // ~219.911
    
    // Gaps entre os segmentos para efeito "flutuante" com extremidades arredondadas (strokeLinecap=round)
    const gapSize = 14;
    const totalGap = chartData.length * gapSize; // 42
    const availableCircumference = circumference - totalGap; // 177.911

    // Calcular comprimentos e offsets dos segmentos
    let cumulativeLength = 0;
    const slices = chartData.map((item, idx) => {
        const percentage = totalClients > 0 ? (item.value / totalClients) * 100 : 0;
        const strokeLength = totalClients > 0 ? (item.value / totalClients) * availableCircumference : 0;
        
        // Rotaciona ligeiramente o início para centralizar o primeiro gap no topo
        const strokeOffset = -(cumulativeLength + (idx * gapSize)) - (gapSize / 2);
        cumulativeLength += strokeLength;

        return {
            ...item,
            percentage,
            strokeLength,
            strokeOffset,
            idx
        };
    });

    return (
        <Card 
            className="neo-stat-card h-100 animate__animated animate__fadeIn" 
            style={{ 
                borderRadius: "16px", 
                border: "2px solid #482712", 
                boxShadow: "0 15px 40px rgba(72, 39, 18, 0.08)",
                background: "#fff",
                fontFamily: "'Outfit', 'Inter', sans-serif"
            }}
        >
            <CardHeader className="p-4 bg-transparent border-bottom-0 pb-1">
                <div className="d-flex align-items-center gap-2">
                    <div
                        style={{ 
                            width: "38px", 
                            height: "38px", 
                            minWidth: "38px",
                            borderRadius: "10px",
                            background: "rgba(226, 102, 28, 0.08)",
                            border: "1px solid rgba(226, 102, 28, 0.20)",
                            color: "#e2661c",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <i className="mdi mdi-chart-donut font-size-18"></i>
                    </div>
                    <div>
                        <h5 className="mb-0 fw-bold font-size-14 text-uppercase" style={{ color: "#482712", letterSpacing: "0.5px" }}>
                            Distribuição de Planos
                        </h5>
                        <small className="text-muted font-size-11 fw-semibold d-block">
                            Breakdown de contas ativas do SaaS
                        </small>
                    </div>
                </div>
            </CardHeader>
            <CardBody className="p-4 d-flex flex-column align-items-center justify-content-center">
                
                {/* 1. Área do Donut */}
                <div className="d-flex justify-content-center position-relative my-2">
                    <div style={{ position: "relative", width: "150px", height: "150px" }}>
                        <svg 
                            width="100%" 
                            height="100%" 
                            viewBox="0 0 100 100" 
                            style={{ transform: "rotate(-90deg)", overflow: "visible" }}
                        >
                            {/* Fundo de vidro translúcido para o trilho interno do donut */}
                            <circle 
                                cx="50" 
                                cy="50" 
                                r={radius} 
                                fill="transparent" 
                                stroke="rgba(72, 39, 18, 0.03)" 
                                strokeWidth="12"
                            />
                            <circle 
                                cx="50" 
                                cy="50" 
                                r="29" 
                                fill="rgba(72, 39, 18, 0.01)" 
                                stroke="rgba(72, 39, 18, 0.04)" 
                                strokeWidth="0.5" 
                            />
                            
                            {slices.map((slice) => {
                                const isHovered = hoveredIdx === slice.idx;
                                return (
                                    <circle
                                        key={slice.name}
                                        cx="50"
                                        cy="50"
                                        r={radius}
                                        fill="transparent"
                                        stroke={slice.color}
                                        strokeWidth={isHovered ? "12" : "8.5"}
                                        strokeLinecap="round"
                                        strokeDasharray={`${slice.strokeLength} ${circumference}`}
                                        strokeDashoffset={slice.strokeOffset}
                                        style={{
                                            transition: "stroke-width 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease",
                                            cursor: "pointer",
                                            opacity: hoveredIdx !== null && !isHovered ? 0.55 : 1
                                        }}
                                        onMouseEnter={() => setHoveredIdx(slice.idx)}
                                        onMouseLeave={() => setHoveredIdx(null)}
                                    />
                                );
                            })}
                        </svg>
                            
                        {/* Texto Central da Rosca */}
                        <div 
                            className="position-absolute d-flex flex-column align-items-center justify-content-center"
                            style={{
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                pointerEvents: "none"
                            }}
                        >
                            <span 
                                className="fw-extrabold font-size-26 m-0 mb-0.5" 
                                style={{ 
                                    color: "#482712", 
                                    lineHeight: 1,
                                    fontFamily: "Outfit, sans-serif" 
                                }}
                            >
                                {totalClients}
                            </span>
                            <span 
                                className="text-muted font-size-9 fw-bold text-uppercase" 
                                style={{ 
                                    letterSpacing: "0.8px",
                                    opacity: 0.8
                                }}
                            >
                                Clientes
                            </span>
                        </div>
                    </div>
                </div>

                {/* 2. Legenda Horizontal (Um ao lado do outro — col-4 com dados centralizados, sem bolinha, nome colorido) */}
                <div className="w-100 mt-4">
                    <div className="row g-2">
                        {slices.map((slice) => {
                            const isHovered = hoveredIdx === slice.idx;
                            return (
                                <div key={slice.name} className="col-4">
                                    <div 
                                        className="p-3 rounded-4 transition-all h-100 d-flex flex-column justify-content-between text-center"
                                        style={{
                                            backgroundColor: isHovered ? "rgba(72, 39, 18, 0.03)" : "rgba(252, 248, 244, 0.35)",
                                            border: "1.5px solid",
                                            borderColor: isHovered ? "rgba(72, 39, 18, 0.15)" : "rgba(72, 39, 18, 0.06)",
                                            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                                            cursor: "pointer"
                                        }}
                                        onMouseEnter={() => setHoveredIdx(slice.idx)}
                                        onMouseLeave={() => setHoveredIdx(null)}
                                    >
                                        <div className="mb-2">
                                            {/* Nome do Plano colorido (sem bolinha) */}
                                            <div className="d-flex align-items-center justify-content-center mb-1.5">
                                                <span 
                                                    className="fw-bold font-size-11 text-uppercase text-truncate" 
                                                    style={{ color: slice.color, letterSpacing: "0.2px" }}
                                                    title={slice.name}
                                                >
                                                    {slice.name}
                                                </span>
                                            </div>
                                            
                                            {/* Percentual grande centralizado */}
                                            <h4 className="fw-bold mb-0 font-size-18" style={{ color: slice.color, fontFamily: "Outfit, sans-serif" }}>
                                                {slice.percentage.toFixed(0)}%
                                            </h4>
                                        </div>
                                        
                                        <div>
                                            <small className="text-muted font-size-9 d-block fw-bold mb-2 text-truncate">
                                                {slice.value} faturas
                                            </small>

                                            {/* Micro barra de progresso proporcional */}
                                            <div 
                                                className="w-100" 
                                                style={{ 
                                                    height: "3.5px", 
                                                    backgroundColor: "rgba(72, 39, 18, 0.06)", 
                                                    borderRadius: "1.75px", 
                                                    overflow: "hidden" 
                                                }}
                                            >
                                                <div 
                                                    style={{ 
                                                        width: `${slice.percentage}%`, 
                                                        height: "100%", 
                                                        backgroundColor: slice.color, 
                                                        borderRadius: "1.75px",
                                                        transition: "width 0.4s ease"
                                                    }} 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

            </CardBody>
        </Card>
    );
};

PlanDistributionChart.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
        color: PropTypes.string.isRequired
    }))
};

export default PlanDistributionChart;
